"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import { useLocale, useTranslations } from "next-intl";
import TransparenciaComponent from "./TransparenciaComponent";
import { fetchTransparencia } from "@/lib/api/datosGenerales/fetchTransparencia";
import type { Transparencia } from "@/lib/api/datosGenerales/types/transparencia";
import { type Locale } from "@/i18n/config";

const HERO_IMAGE =
  "https://galeria.tocumenaero.com/gallery/api/Terminal%201%20y%202%20fachada%20d%C3%ADa%20y%20noche/321A5589.jpg";

function Hero() {
  const t = useTranslations("transparenciaPage");
  return (
    <section className="relative h-[55vh] min-h-[420px] flex items-end overflow-hidden">
      <Image
        src={HERO_IMAGE}
        alt={t("title")}
        fill
        className="object-cover"
        priority
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/10" />
      <div className="relative z-10 max-w-[80%] mx-auto w-full pb-16 md:pb-20">
        <div className="max-w-3xl">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white font-semibold mb-4">
            <span className="w-8 h-px bg-white" />
            {t("eyebrow")}
          </span>
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-4 tracking-tight text-balance">
            {t("title")}
          </h1>
          <p className="text-white/80 text-base md:text-lg leading-relaxed">
            {t("subtitle")}
          </p>
        </div>
      </div>
    </section>
  );
}

export default function TransparenciaPage() {
  const t = useTranslations("transparenciaPage");
  const locale = useLocale() as Locale;
  const [data, setData] = useState<Transparencia | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const loadData = async () => {
      try {
        const raw = await fetchTransparencia(locale);
        if (!cancelled) setData(raw);
      } catch (err) {
        console.error("Error cargando transparencia:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadData();
    return () => {
      cancelled = true;
    };
  }, [locale]);

  return (
    <main className="min-h-screen bg-white">
      <Hero />

      {loading ? (
        <section className="max-w-[80%] mx-auto py-16">
          <div className="space-y-4">
            <div className="h-6 w-1/2 bg-gray-100 rounded animate-pulse" />
            <div className="h-4 w-3/4 bg-gray-100 rounded animate-pulse" />
            <div className="h-4 w-2/3 bg-gray-100 rounded animate-pulse" />
          </div>
        </section>
      ) : !data ? (
        <section className="max-w-[80%] mx-auto py-24 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-3 tracking-tight">
            {t("errorTitle")}
          </h2>
          <p className="text-gray-600">{t("errorMessage")}</p>
        </section>
      ) : (
        <section className="max-w-[80%] mx-auto py-16">
          <TransparenciaComponent {...data} />
        </section>
      )}
    </main>
  );
}
